import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import { formatRange } from '@jupi/ui'
import { useTransferTargets, type Booking, type TransferTarget } from '../api/queries'

// Hand a reservation to another member: pick them, confirm. They are told
// by email; there is nothing for them to accept.
export function TransferBookingDialog({
  booking,
  busy,
  onConfirm,
  onClose,
}: {
  booking: Booking | null
  busy: boolean
  onConfirm: (target: TransferTarget) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { data: members = [] } = useTransferTargets(booking !== null)
  const [target, setTarget] = useState<TransferTarget | null>(null)

  return (
    <Dialog open={booking !== null} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('booking.transfer.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {booking
            ? t('booking.transfer.message', {
                aircraft: booking.registration,
                when: formatRange(booking.startsAt, booking.endsAt),
              })
            : ''}
        </DialogContentText>
        <Autocomplete
          options={members}
          value={target}
          onChange={(_, next) => setTarget(next)}
          getOptionLabel={(member) => member.displayName}
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          renderInput={(params) => <TextField {...params} autoFocus label={t('booking.transfer.member')} />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button variant="contained" disabled={!target || busy} onClick={() => target && onConfirm(target)}>
          {t('booking.transfer.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
